import {
  Pagination,
  PaginationDto,
  paginationFromDto,
} from '../common/models/pagination'
import { SiteID } from '../sites/models/site'
import { Tag, TagDto, tagFromDto } from './models/tag'

import axiosInstance from '../axiosInstance'

const TagEndpoints = {
  base: (siteId: SiteID) => `api/orgs/${siteId}/tags` as const,
  index: (siteId: SiteID) => `${TagEndpoints.base(siteId)}` as const,
  create: (siteId: SiteID) => `${TagEndpoints.base(siteId)}` as const,
}

export interface TagSearchForm {
  name?: string
  types?: string[]

  taggableId?: string
  taggableType?: 'employee' | 'shift'
  withoutTaggableId?: string

  perPage: number
  page: number
}

interface TagSearchParams {
  name?: string
  types?: string[]

  taggable_id?: string
  taggable_type?: 'employee' | 'shift'
  without_taggable_id?: string

  per_page: number
  page: number
}

function searchFormToParams(form: TagSearchForm): TagSearchParams {
  const params: TagSearchParams = {
    per_page: form.perPage,
    page: form.page,
  }

  if (form.name) {
    params.name = form.name
  }

  if (form.types && form.types.length > 0) {
    params.types = form.types
  }

  if (form.taggableId) {
    params.taggable_id = form.taggableId
  }

  if (form.taggableType) {
    params.taggable_type = form.taggableType
  }

  if (form.withoutTaggableId) {
    params.without_taggable_id = form.withoutTaggableId
  }

  return params
}

export const fetch = async (
  siteId: SiteID,
  form: TagSearchForm,
): Promise<{ tags: Tag[]; pagination: Pagination }> => {
  const params = searchFormToParams(form)

  const response = await axiosInstance.get(TagEndpoints.index(siteId), {
    params,
  })

  const data = response.data.data as TagDto[]
  const meta = response.data.meta as { pagination: PaginationDto }

  return {
    tags: data.map((tag) => {
      return tagFromDto(tag)
    }),
    pagination: paginationFromDto(meta.pagination),
  }
}

export interface TagCreateForm {
  name: string
  type: string
}

interface TagCreateData {
  name: string
  type: string
}

function createFormToData(form: TagCreateForm): TagCreateData {
  const data: TagCreateData = {
    name: form.name,
    type: form.type,
  }

  return data
}

export const create = async (
  siteId: SiteID,
  form: TagCreateForm,
): Promise<{ tag: Tag }> => {
  const data = createFormToData(form)

  const response = await axiosInstance.post(TagEndpoints.create(siteId), data)

  const result = response.data.data as TagDto

  return {
    tag: tagFromDto(result),
  }
}

export const fetchForTaggable = async (
  siteId: SiteID,
  form: TagSearchForm,
): Promise<{ tags: Tag[]; pagination: Pagination }> => {
  const params = searchFormToParams(form)

  const response = await axiosInstance.get(
    `api/orgs/${siteId}/tags/taggables`,
    { params },
  )

  const data = response.data.data as TagDto[]
  const meta = response.data.meta as { pagination: PaginationDto }

  return {
    tags: data.map((tag) => {
      return tagFromDto(tag)
    }),
    pagination: paginationFromDto(meta.pagination),
  }
}

export interface TagAttachForm {
  tag: string
  targetType: 'employee' | 'shift'
  targetId: string
}

interface TagAttachData {
  tag: string
  target_type: 'employee' | 'shift'
  target_id: string
}

function attachFormToData(form: TagAttachForm): TagAttachData {
  const data: TagAttachData = {
    tag: form.tag,
    target_type: form.targetType,
    target_id: form.targetId,
  }

  return data
}

export const attach = async (
  siteId: SiteID,
  form: TagAttachForm,
): Promise<void> => {
  const data = attachFormToData(form)

  await axiosInstance.post(`api/orgs/${siteId}/tags/taggables`, data)

  return
}

export const detach = async (
  siteId: SiteID,
  form: TagAttachForm,
): Promise<void> => {
  const data = attachFormToData(form)

  await axiosInstance.delete(`api/orgs/${siteId}/tags/taggables`, { data })

  return
}
